import React, { createContext, useContext, useState } from 'react';

export const formatCurrency = (num) =>
  '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Helpers to add item-level resolution fields to freshly-built data
const withItemFields = (items) =>
  items.map((item) => ({ cleared: false, resolutionMode: null, resolutionReason: null, ...item }));

const buildInitialEntries = () => [
  {
    id: 'CBP-8829102A',
    date: '2026-03-12',
    broker: 'Expeditors',
    recoveryValue: 4500.00,
    deficiencyValue: 5450.00,
    indicators: ['fail', 'fail', 'hitl'],
    // Pillar 1: 4 vectors
    classVectors: [
      { name: 'Pivot Row vs. 7501 Line Reconciliation', status: 'pass' },
      { name: '7501 Internal Arithmetic Integrity (Box 35)', status: 'pass' },
      { name: 'Base HTS & Duty Rate Integrity', status: 'pass' },
      { name: 'Section 122 Offsets', status: 'pass' },
    ],
    // Pillar 2: 8 vectors
    typeAVectors: [
      { name: 'Chapter 99 & Sec 232 / 301 Audits', status: 'recovery' },
      { name: 'Exclusion Monitoring (USTR)', status: 'pass' },
      { name: 'Incoterm / Freight Valuation Variance', status: 'risk' },
      { name: 'Special Program Indicator (SPI) Omissions', status: 'pass' },
      { name: 'Country of Origin (COO) Consistency', status: 'pass' },
      { name: 'MPF / HMF Cap Violations', status: 'pass' },
      { name: 'UOM / Quantity Integrity (Stats. Anomaly)', status: 'pass' },
      { name: 'Date of Export / FX Conversion', status: 'risk' },
    ],
    typeBVectors: [
      { name: 'Assists & Royalties Predictor', status: 'hitl' },
      { name: 'Buying Commission Predictor', status: 'pass' },
      { name: 'Related Party / Transfer Pricing Predictor', status: 'pass' },
      { name: 'First Sale for Export Predictor', status: 'pass' },
    ],
    emailTo: 'imports@expeditors.com',
    emailSubject: 'PSC Required: Entry CBP-8829102A - Form 7501 Corrections',
    exceptions: [
      {
        title: 'Triage Required: Chapter 99 Audits',
        type: 'recovery',
        value: 4500.00,
        cleared: false,
        items: withItemFields([
          { part: 'PN-8890A Actuator Assembly', declared: '9903.88.01 (25%)', target: '9902.14.00 (0%)', flag: 'Ch 99 Mismatch', vectorTarget: 'Chapter 99 & Sec 232 / 301 Audits' },
        ]),
        summary: '<strong>[RECOVERY ALERT] Statutory exemption missed.</strong> Active USTR exclusion found. Duty Base: $18,000. Broker paid 25%. Correct rate is 0%.',
        emailSnippet: '<strong>[RECOVERY ALERT] Chapter 99 Error:</strong> Statutory exemption missed. Active USTR exclusion found. Please claim the correct 0% rate, resulting in a <strong>$4,500 Duty Recovery</strong>.<br><br>',
      },
      {
        title: 'Triage Required: Freight Valuation Variance',
        type: 'risk',
        value: 450.00,
        cleared: false,
        items: withItemFields([
          { part: 'Header Level (Freight)', declared: '$4,500.00 Deducted', target: '$0.00 Deducted (FCA)', flag: 'Illegal Deduction', vectorTarget: 'Incoterm / Freight Valuation Variance' },
        ]),
        summary: '<strong>[COMPLIANCE RISK] Valuation Error:</strong> Broker illegally deducted freight charges from an FCA invoice. Duty base undervalued by $4,500.',
        emailSnippet: '<strong>[COMPLIANCE RISK] Valuation Error:</strong> Broker illegally deducted freight charges from an FCA invoice. Please remit the resulting <strong>$450 Duty Deficiency</strong>.<br><br>',
      },
      {
        title: 'Triage Required: FX Conversion',
        type: 'risk',
        value: 5000.00,
        cleared: false,
        items: withItemFields([
          { part: 'FX Rate Date', declared: '2026-03-15 (Arrival)', target: '2026-02-20 (Sailing)', flag: 'FX Date Error', vectorTarget: 'Date of Export / FX Conversion' },
        ]),
        summary: '<strong>[COMPLIANCE RISK] FX Date Error.</strong> Broker used Arrival Date instead of actual Sailing Date for a JPY invoice. Undervalued by $50k.',
        emailSnippet: '<strong>[COMPLIANCE RISK] FX Date Error:</strong> Broker used Arrival Date instead of actual Sailing Date for a JPY invoice. Please correct the date of export and remit the <strong>$5,000 Duty Deficiency</strong>.<br><br>',
      },
    ],
    hitl: [
      {
        title: 'Agentic Predictor: Assists & Royalties',
        desc: '[HITL REQUIRED] Potential Assist/Royalty detected. Verify if undeclared tooling costs must be added to the dutiable value.',
        actionTitle: 'Verification Required:',
        actionDesc: 'A tooling amortization line was flagged on the Commercial Invoice.',
        vectorsTarget: 'Assists & Royalties Predictor',
        type: 'hitl',
        value: 0,
        cleared: false,
        validated: false,
        resolutionMode: null,
        resolutionReason: null,
      },
    ],
  },
  {
    id: 'CBP-4492019B',
    date: '2026-03-14',
    broker: 'Kuehne+Nagel',
    recoveryValue: 0.00,
    deficiencyValue: 0.00,
    indicators: ['pass', 'pass', 'pass'],
    classVectors: [
      { name: 'Pivot Row vs. 7501 Line Reconciliation', status: 'pass' },
      { name: '7501 Internal Arithmetic Integrity (Box 35)', status: 'pass' },
      { name: 'Base HTS & Duty Rate Integrity', status: 'pass' },
      { name: 'Section 122 Offsets', status: 'pass' },
    ],
    typeAVectors: [
      { name: 'Chapter 99 & Sec 232 / 301 Audits', status: 'pass' },
      { name: 'Exclusion Monitoring (USTR)', status: 'pass' },
      { name: 'Incoterm / Freight Valuation Variance', status: 'pass' },
      { name: 'Special Program Indicator (SPI) Omissions', status: 'pass' },
      { name: 'Country of Origin (COO) Consistency', status: 'pass' },
      { name: 'MPF / HMF Cap Violations', status: 'pass' },
      { name: 'UOM / Quantity Integrity (Stats. Anomaly)', status: 'pass' },
      { name: 'Date of Export / FX Conversion', status: 'pass' },
    ],
    typeBVectors: [
      { name: 'Assists & Royalties Predictor', status: 'pass' },
      { name: 'Related Party / Transfer Pricing Predictor', status: 'pass' },
      { name: 'Buying Commission Predictor', status: 'pass' },
      { name: 'First Sale for Export Predictor', status: 'pass' },
    ],
    emailTo: '',
    emailSubject: '',
    exceptions: [],
    hitl: [],
  },
  {
    id: 'CBP-7734110C',
    date: '2026-03-15',
    broker: 'CH Robinson',
    recoveryValue: 0.00,
    deficiencyValue: 0.00,
    indicators: ['fail', 'fail', 'pass'],
    classVectors: [
      { name: 'Pivot Row vs. 7501 Line Reconciliation', status: 'risk' },
      { name: '7501 Internal Arithmetic Integrity (Box 35)', status: 'pass' },
      { name: 'Base HTS & Duty Rate Integrity', status: 'pass' },
      { name: 'Section 122 Offsets', status: 'pass' },
    ],
    typeAVectors: [
      { name: 'Chapter 99 & Sec 232 / 301 Audits', status: 'pass' },
      { name: 'Exclusion Monitoring (USTR)', status: 'pass' },
      { name: 'Incoterm / Freight Valuation Variance', status: 'pass' },
      { name: 'Special Program Indicator (SPI) Omissions', status: 'pass' },
      { name: 'Country of Origin (COO) Consistency', status: 'pass' },
      { name: 'MPF / HMF Cap Violations', status: 'pass' },
      { name: 'UOM / Quantity Integrity (Stats. Anomaly)', status: 'risk' },
      { name: 'Date of Export / FX Conversion', status: 'pass' },
    ],
    typeBVectors: [
      { name: 'Assists & Royalties Predictor', status: 'pass' },
      { name: 'Related Party / Transfer Pricing Predictor', status: 'pass' },
      { name: 'Buying Commission Predictor', status: 'pass' },
      { name: 'First Sale for Export Predictor', status: 'pass' },
    ],
    emailTo: 'compliance@chrobinson.com',
    emailSubject: 'PSC Required: Entry CBP-7734110C - Form 7501 Corrections',
    exceptions: [
      {
        title: 'Triage Required: Pivot Row vs. 7501 Line Reconciliation',
        type: 'risk',
        value: 0,
        cleared: false,
        items: withItemFields([
          { part: 'Entry Header', declared: '14 Lines Filed', target: '15 Rows in Pivot', flag: 'Dropped Line Item', vectorTarget: 'Pivot Row vs. 7501 Line Reconciliation' },
        ]),
        summary: '[COMPLIANCE RISK] Structural mismatch detected. Pivot instructed [15] lines; Broker filed [14] lines. Unauthorized line-item consolidation or omission. CF-28 Audit Risk.',
        emailSnippet: '<strong>[COMPLIANCE RISK] Line Reconciliation Error:</strong> Structural mismatch detected. Pivot instructed [15] lines; Broker filed [14] lines. Unauthorized line-item consolidation or omission. CF-28 Audit Risk.<br><br>',
      },
      {
        title: 'Triage Required: UOM / Quantity Integrity (Stats. Anomaly)',
        type: 'risk',
        value: 0,
        cleared: false,
        items: withItemFields([
          { part: 'PN-4412B Titanium Landing Strut', declared: '1,000 KGS', target: '1,000 LBS', flag: 'UOM Mismatch', vectorTarget: 'UOM / Quantity Integrity (Stats. Anomaly)' },
        ]),
        summary: '[COMPLIANCE RISK] UOM reporting mismatch. Broker filed [KGS]. USITC database strictly mandates [LBS] for HTS [8803.30.00]. Census Bureau statistical reporting violation.',
        emailSnippet: '<strong>[COMPLIANCE RISK] UOM Error:</strong> UOM reporting mismatch. Broker filed [KGS]. USITC database strictly mandates [LBS] for HTS [8803.30.00]. Census Bureau statistical reporting violation.<br><br>',
      },
    ],
    hitl: [],
  },
];

const DASIContext = createContext(null);

export const DASIProvider = ({ children }) => {
  const [entries, setEntries] = useState(buildInitialEntries);
  const [currentIndex, setCurrentIndex] = useState(0);

  // ── New per-item resolution action ──────────────────────────────────────
  const resolveExceptionItem = (entryIdx, exIdx, itemIdx, mode, reason) => {
    setEntries((prev) =>
      prev.map((e, i) => {
        if (i !== entryIdx) return e;

        // Update the specific item
        const newExceptions = e.exceptions.map((ex, j) => {
          if (j !== exIdx) return ex;
          const newItems = ex.items.map((item, k) =>
            k !== itemIdx
              ? item
              : { ...item, cleared: true, resolutionMode: mode, resolutionReason: reason }
          );
          const allCleared = newItems.every((item) => item.cleared);
          return { ...ex, items: newItems, cleared: allCleared };
        });

        const exception = newExceptions[exIdx];
        let newClassVectors = e.classVectors;
        let newTypeAVectors = e.typeAVectors;
        let newTypeBVectors = e.typeBVectors;
        let newRecovery = e.recoveryValue;
        let newDeficiency = e.deficiencyValue;

        if (exception.cleared) {
          const vTarget = exception.items[itemIdx]?.vectorTarget;
          const anyAccepted = exception.items.some((it) => it.resolutionMode === 'accept');
          const vectorStatus = anyAccepted ? 'accepted' : 'overridden';

          if (vTarget) {
            const upd = (arr) =>
              arr.map((v) => (v.name === vTarget ? { ...v, status: vectorStatus } : v));
            newClassVectors = upd(newClassVectors);
            newTypeAVectors = upd(newTypeAVectors);
            newTypeBVectors = upd(newTypeBVectors);
          }

          // Only reduce financials if the exception was fully overridden
          if (!anyAccepted && exception.value > 0) {
            if (exception.type === 'recovery')
              newRecovery = Math.max(0, newRecovery - exception.value);
            else if (exception.type === 'risk')
              newDeficiency = Math.max(0, newDeficiency - exception.value);
          }
        }

        return {
          ...e,
          exceptions: newExceptions,
          classVectors: newClassVectors,
          typeAVectors: newTypeAVectors,
          typeBVectors: newTypeBVectors,
          recoveryValue: newRecovery,
          deficiencyValue: newDeficiency,
        };
      })
    );
  };

  // ── New HITL resolution action ───────────────────────────────────────────
  const resolveHitl = (entryIdx, hitlIdx, mode, reason) => {
    setEntries((prev) =>
      prev.map((e, i) => {
        if (i !== entryIdx) return e;
        const hitlItem = e.hitl[hitlIdx];
        const vTarget = hitlItem?.vectorsTarget;
        const vectorStatus = mode === 'accept' ? 'accepted' : 'overridden';

        let newDeficiency = e.deficiencyValue;
        if (mode === 'accept') {
          const rawVal = reason.replace(/[^0-9.]/g, '');
          const parsedVal = parseFloat(rawVal);
          if (!isNaN(parsedVal) && parsedVal > 0) newDeficiency += parsedVal;
        }

        const upd = (arr) =>
          !vTarget ? arr : arr.map((v) => (v.name === vTarget ? { ...v, status: vectorStatus } : v));

        return {
          ...e,
          hitl: e.hitl.map((h, j) =>
            j !== hitlIdx
              ? h
              : { ...h, cleared: true, resolutionMode: mode, resolutionReason: reason }
          ),
          classVectors: upd(e.classVectors),
          typeAVectors: upd(e.typeAVectors),
          typeBVectors: upd(e.typeBVectors),
          deficiencyValue: newDeficiency,
        };
      })
    );
  };

  // ── Legacy actions (kept for backward compat) ────────────────────────────
  const clearException = (entryIndex, exceptionIndex, vectorTarget, exceptionValue, exceptionType) => {
    resolveExceptionItem(entryIndex, exceptionIndex, 0, 'accept', 'Cleared');
  };

  const clearHitl = (entryIndex, hitlIndex) => {
    resolveHitl(entryIndex, hitlIndex, 'override', 'Cleared');
  };

  const validateHitl = (entryIndex, hitlIndex) => {
    setEntries((prev) =>
      prev.map((e, i) => {
        if (i !== entryIndex) return e;
        return {
          ...e,
          hitl: e.hitl.map((h, j) => (j === hitlIndex ? { ...h, validated: true } : h)),
        };
      })
    );
  };

  return (
    <DASIContext.Provider
      value={{
        entries,
        currentIndex,
        setCurrentIndex,
        resolveExceptionItem,
        resolveHitl,
        clearException,
        clearHitl,
        validateHitl,
      }}
    >
      {children}
    </DASIContext.Provider>
  );
};

export const useDASI = () => useContext(DASIContext);
